import { Vec2 } from '../utils/math';
import { Detector } from './Component';
import { Scene } from './Scene';
import { RayTracer } from '../physics/RayTracer';
import { HybridRenderer } from '../physics/HybridRenderer';
import { FieldGrid } from '../physics/FieldGrid';
import { RayRenderer } from '../rendering/RayRenderer';
import { FieldRenderer } from '../rendering/FieldRenderer';
import { BloomPass } from '../rendering/BloomPass';
import { TracedSegment } from './Ray';
import { History } from './History';
import { Camera } from './Camera';

export class LightEngine {
  scene: Scene;
  private rayTracer: RayTracer;
  private hybridRenderer: HybridRenderer;
  private renderer: RayRenderer;
  private fieldRenderer: FieldRenderer | null = null;
  private _tracedSegments: TracedSegment[] = [];
  private _activeFieldGrids: FieldGrid[] = [];
  private dirty: boolean = true;
  private animationId: number = 0;
  private selectedComponentId?: number;
  private snapIndicator: { position: Vec2; hint: string } | null = null;
  private _fdtdEnabled: boolean = false;
  private showFieldDebug: boolean = false;
  readonly history: History = new History();
  readonly camera: Camera = new Camera();
  readonly bloomPass: BloomPass = new BloomPass();
  private canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.scene = new Scene();
    this.rayTracer = new RayTracer();
    this.hybridRenderer = new HybridRenderer();
    this.renderer = new RayRenderer(canvas);

    // FieldRenderer shares the same 2D context
    const ctx = canvas.getContext('2d');
    if (ctx) {
      this.fieldRenderer = new FieldRenderer(ctx);
    }
  }

  get tracedSegments(): TracedSegment[] {
    return this._tracedSegments;
  }

  get activeFieldGrids(): FieldGrid[] {
    return this._activeFieldGrids;
  }

  get selectedId(): number | undefined {
    return this.selectedComponentId;
  }

  set selectedId(id: number | undefined) {
    this.selectedComponentId = id;
    this.dirty = true;
  }

  get fdtdEnabled(): boolean {
    return this._fdtdEnabled;
  }

  set fdtdEnabled(enabled: boolean) {
    this._fdtdEnabled = enabled;
    this.hybridRenderer.setEnabled(enabled);
    this.dirty = true;
  }

  toggleFieldDebug(): void {
    this.showFieldDebug = !this.showFieldDebug;
    this.dirty = true;
  }

  toggleFieldViewMode(): void {
    if (this.fieldRenderer) {
      this.fieldRenderer.viewMode = this.fieldRenderer.viewMode === 'intensity' ? 'amplitude' : 'intensity';
      this.dirty = true;
    }
  }

  toggleBloom(): void {
    this.bloomPass.setEnabled(!this.bloomPass.isEnabled());
    this.dirty = true;
  }

  // Grid placement
  gridEnabled: boolean = false;
  gridSpacing: number = 20;
  private readonly gridSpacings = [10, 20, 50];

  toggleGrid(): void {
    this.gridEnabled = !this.gridEnabled;
    this.dirty = true;
  }

  cycleGridSpacing(): void {
    const idx = this.gridSpacings.indexOf(this.gridSpacing);
    this.gridSpacing = this.gridSpacings[(idx + 1) % this.gridSpacings.length];
    this.dirty = true;
  }

  // Phase color visualization
  phaseColorMode: boolean = false;

  togglePhaseColor(): void {
    this.phaseColorMode = !this.phaseColorMode;
    this.dirty = true;
  }

  markDirty(): void {
    this.dirty = true;
  }

  /** Save current scene state to undo history (call before mutations) */
  pushHistory(): void {
    this.history.push(this.scene);
  }

  undo(): boolean {
    const restored = this.history.undo(this.scene);
    if (!restored) return false;
    this.scene = restored;
    this.selectedComponentId = undefined;
    this.dirty = true;
    return true;
  }

  redo(): boolean {
    const restored = this.history.redo(this.scene);
    if (!restored) return false;
    this.scene = restored;
    this.selectedComponentId = undefined;
    this.dirty = true;
    return true;
  }

  /** Replace the entire scene (for load operations) */
  replaceScene(scene: Scene): void {
    this.scene = scene;
    this.selectedComponentId = undefined;
    this.history.clear();
    this.dirty = true;
  }

  start(): void {
    this.renderer.resize();
    window.addEventListener('resize', () => {
      this.renderer.resize();
      this.dirty = true;
    });
    this.loop();
  }

  stop(): void {
    cancelAnimationFrame(this.animationId);
  }

  getRenderer(): RayRenderer {
    return this.renderer;
  }

  getHybridRenderer(): HybridRenderer {
    return this.hybridRenderer;
  }

  setSnapIndicator(position: Vec2 | null, hint: string = ''): void {
    this.snapIndicator = position ? { position, hint } : null;
  }

  private loop = (): void => {
    let needsRender = false;

    if (this.dirty) {
      this.dirty = false;
      // Re-trace rays only when scene changes
      this._tracedSegments = this.rayTracer.trace(this.scene);
      needsRender = true;

      // When scene changes and FDTD is on, re-initialize field grids
      if (this._fdtdEnabled) {
        this._activeFieldGrids = this.hybridRenderer.update(this._tracedSegments);
      } else {
        this._activeFieldGrids = [];
      }
    }

    // FDTD advances independently — doesn't re-trace rays every frame
    if (this._fdtdEnabled && this._activeFieldGrids.length > 0) {
      const t0 = performance.now();
      this.hybridRenderer.stepFDTD(this._activeFieldGrids);
      const elapsed = performance.now() - t0;

      // Cap: if FDTD took >8ms, it'll get another shot next frame
      if (elapsed < 8) {
        needsRender = true;
      } else {
        needsRender = true; // still render, just don't do extra steps
      }
    }

    // Sample detector intensity each frame for time-domain display
    for (const comp of this.scene.getAll()) {
      if (comp instanceof Detector) comp.sampleIntensity();
    }

    if (needsRender) {
      this.render();
    }

    this.animationId = requestAnimationFrame(this.loop);
  };

  private render(): void {
    const canvasSize = this.renderer.getCanvasSize();
    this.renderer.clear();

    // Apply camera transform for all world-space rendering
    this.renderer.applyCamera(this.camera, canvasSize.width, canvasSize.height);

    // Draw placement grid
    if (this.gridEnabled) {
      this.renderer.drawGrid(this.camera, canvasSize.width, canvasSize.height, this.gridSpacing);
    }

    // Draw field grid overlays (behind rays)
    if (this.fieldRenderer && this._activeFieldGrids.length > 0) {
      this.fieldRenderer.drawFieldGrids(this._activeFieldGrids);
      if (this.showFieldDebug) {
        this.fieldRenderer.drawGridBounds(this._activeFieldGrids);
      }
    }

    this.renderer.drawRays(this._tracedSegments, this.phaseColorMode);
    this.renderer.drawComponents(this.scene.getAll(), this.selectedComponentId);

    if (this.snapIndicator) {
      this.renderer.drawSnapIndicator(this.snapIndicator.position, this.snapIndicator.hint);
    }

    // Reset transform for any screen-space UI
    this.renderer.resetTransform();

    // Apply bloom post-processing
    this.bloomPass.apply(this.canvas);
  }
}
